// @ts-check

/**
 * @file Renders task timelogs and timelog row actions.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import errorMessage from "../../../src/shared/error-message"
import Pagination from "../../../src/components/pagination"
import {optionalRouteParam, requiredRouteParam} from "../../../src/shared/route-params"
import ScreenTitle from "../../../src/components/screen-title"
import {Task} from "../../../src/frontend-models/task"
import TaskSubnav from "../../../src/screens/tasks/subnav"
import {emptyTaskSubnavCounts, taskSubnavCountConfig, taskSubnavCountsFromTask} from "../../../src/screens/tasks/subnav-counts"
import Text from "@kaspernj/api-maker/build/utils/text"
import {Timelog} from "../../../src/frontend-models/timelog"
import {pathForQueryParamUpdates, pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import MemoizedTimelogRow from "../../../src/components/tasks/timelog-row"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("../../../src/screens/tasks/subnav-counts").TaskSubnavCounts} TaskSubnavCounts */
/** @typedef {Record<string, string | string[] | undefined>} RouteParams */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */

/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30
const TIMELOG_INDEX_PRELOAD_SELECT = {
  User: ["id", "name"]
}
const TIMELOG_INDEX_SELECT = ["comment", "date", "id", "invoiced", "taskId", "timeSeconds", "userId"]
const TASK_TIMELOGS_QUERY_PARAM_SELECTOR = ["page"]
const QUERY_PARAM_UPDATE_OPTIONS = {excludeKeys: ["id"]}

/** @typedef {{subnavCounts: TaskSubnavCounts, task: Task | null, timelogs: Timelog[], totalCount: number}} CachedTasksTimelogsRouteState */

/** @type {Map<string, CachedTasksTimelogsRouteState>} */
const routeStateCache = new Map()

/** @typedef {Record<string, never>} TasksTimelogsScreenProps */

/**
 * @typedef {object} TasksTimelogsScreenState
 * @property {Timelog[]} timelogs Loaded timelog records.
 * @property {string} timelogsTaskID Task id for the currently rendered timelog records.
 * @property {boolean} loading Whether timelogs are loading.
 * @property {number} page Current timelogs page.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {TaskSubnavCounts} subnavCounts Task subnav counts.
 * @property {Task | null} task Loaded task record.
 * @property {number} totalCount Total matching timelogs count.
 */

/**
 * Lists timelogs for a single task.
 * @augments {ShapeComponent<TasksTimelogsScreenProps, TasksTimelogsScreenState>}
 */
class TasksTimelogsScreen extends ShapeComponent {
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteParams} */
  params = {}
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string} */
  taskID = ""
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {string} */
  hydratedTaskID = ""
  /** @type {number} */
  lastAppliedRoutePage = 0
  loadRequestId = 0
  /** @type {TasksTimelogsScreenState} */
  state = {
    timelogs: /** @type {Timelog[]} */ ([]),
    timelogsTaskID: "",
    loading: true,
    page: 1,
    status: /** @type {{kind: "error", text: string} | null} */ (null),
    subnavCounts: emptyTaskSubnavCounts(),
    task: /** @type {Task | null} */ (null),
    totalCount: 0
  }

  /** Initializes translations, route params, and timelog loading. */
  setup() {
    useLocale()
    const routeParams = useLocalSearchParams()
    const queryParams = /** @type {RouteQueryParams} */ (useSelectedQueryParams(TASK_TIMELOGS_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()

    const taskID = requiredRouteParam(routeParams, "id", "Task ID")
    const routePage = pageFromParams(queryParams)
    this.currentQueryParams = currentQueryParams
    this.params = routeParams
    this.router = router
    this.taskID = taskID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    if (this.hydratedTaskID !== taskID) {
      this.hydratedTaskID = taskID
      this.hydrateCachedRouteState(taskID)
    }
    if (this.lastAppliedRoutePage !== routePage) {
      this.lastAppliedRoutePage = routePage
      this.state.page = routePage
    }
    const page = this.s.page
    useEffect(() => {
      let cancelled = false
      void this.tt.loadTimelogs(taskID, page, () => cancelled)
      return () => {
        cancelled = true
      }
    }, [taskID, page])
  }

  /**
   * Renders the task timelogs screen.
   * @returns {ReactNode} Task timelogs content.
   */
  render() {
    const {timelogs, loading, status, subnavCounts, task} = this.s
    const rowsBelongToCurrentTask = this.s.timelogsTaskID === this.tt.taskID
    const visibleTimelogs = rowsBelongToCurrentTask ? timelogs : []
    const hasTimelogs = visibleTimelogs.length > 0
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const projectID = task ? task.projectId() || "" : ""
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="tasksTimelogsScreen"
      >
        <ScreenTitle>{_("Timelogs")}</ScreenTitle>
        <View
          testID="tasksTimelogsScreen/content"
        >
          <View
            style={styles.headerRow ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 16
            }}
            testID="tasksTimelogsScreen/headerRow"
          >
            <Text
              style={styles.title ||= {
                fontSize: 24,
                fontWeight: 700
              }}
              testID="tasksTimelogsScreen/title"
            >
              {_("Timelogs")}
            </Text>
            <View
              style={styles.headerActions ||= {
                flexDirection: "row",
                gap: 8
              }}
              testID="tasksTimelogsScreen/headerActions"
            >
              <Button
                href={buildRoutePath(this.params, `/timelogs/new?task_id=${encodeURIComponent(this.taskID)}`)}
                iconName="plus"
                label={_("New timelog")}
                testID="tasksTimelogsScreen/newTimelogButton"
              />
              <Button
                href={buildRoutePath(
                  this.params,
                  projectID
                    ? `/tasks/${this.taskID}/edit?projectId=${encodeURIComponent(projectID)}`
                    : `/tasks/${this.taskID}/edit`
                )}
                iconName="pen-to-square"
                label={_("Edit")}
                testID="tasksTimelogsScreen/editButton"
              />
            </View>
          </View>
          {task &&
            <TaskSubnav
              activeItem="timelogs"
              assignedUsersCount={subnavCounts.assignedUsersCount}
              commentsCount={subnavCounts.commentsCount}
              params={this.params}
              projectId={projectID}
              taskId={this.taskID}
              testID="tasksTimelogsScreen/subnav"
              timelogsCount={subnavCounts.timelogsCount}
            />
          }
          {status &&
            <Text
              style={styles[`statusError-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="tasksTimelogsScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading && !hasTimelogs &&
            <Text
              style={styles[`loadingText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="tasksTimelogsScreen/loadingText"
            >
              {_("Loading...")}
            </Text>
          }
          {!loading && !hasTimelogs &&
            <Text
              style={styles[`emptyText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="tasksTimelogsScreen/emptyText"
            >
              {_("No timelogs found")}
            </Text>
          }
          {hasTimelogs && visibleTimelogs.map((timelog) => (
            <MemoizedTimelogRow
              key={timelog.id()}
              timelog={timelog}
              params={this.params}
              onDeleted={this.tt.onTimelogDeleted}
            />
          ))}
          {this.tt.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders pagination when rows are loaded.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, timelogs, totalCount} = this.s
    if (this.s.timelogsTaskID !== this.tt.taskID) return null
    if (loading && timelogs.length === 0) return null
    return (
      <Pagination
        disabled={loading}
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="tasksTimelogsScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Loads the requested task timelog page.
   * @param {string} taskID Task identifier.
   * @param {number} page Page to load.
   * @param {() => boolean} cancelled Whether the load has been cancelled.
   * @returns {Promise<void>} Resolves after timelogs are loaded.
   * @rejects {unknown} When loading fails.
   */
  loadTimelogs = async (taskID, page, cancelled) => {
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      const baseQuery = Timelog
        .where({taskId: taskID})
        .order([["date", "desc"], ["id", "desc"]])
      const [timelogs, totalCount, task] = await Promise.all([
        baseQuery
          .clone()
          .preload("user")
          .select(TIMELOG_INDEX_SELECT)
          .select(TIMELOG_INDEX_PRELOAD_SELECT)
          .abilities(["update", "destroy"])
          .page(page)
          .perPage(PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count(),
        Task
          .where({id: taskID})
          .withCount(taskSubnavCountConfig())
          .first()
      ])
      if (cancelled() || this.loadRequestId !== requestId) return
      const subnavCounts = task ? taskSubnavCountsFromTask(task) : emptyTaskSubnavCounts()
      const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
      if (page > totalPages) {
        this.s.totalCount = totalCount
        this.s.subnavCounts = subnavCounts
        this.s.task = task
        this.s.page = totalPages
        this.tt.replaceRoutePage(totalPages)
        return
      }
      this.s.timelogs = timelogs
      this.s.timelogsTaskID = taskID
      this.s.totalCount = totalCount
      this.s.subnavCounts = subnavCounts
      this.s.task = task
      this.s.loading = false
      this.cacheLoadedRouteState()
    } catch (error) {
      if (cancelled() || this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }

  /**
   * Updates the active timelogs page.
   * @param {number} nextPage Page selected by pagination.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.tt.pushRoutePage(nextPage)
    this.s.page = nextPage
  }

  /**
   * Pushes a user-selected page into route state.
   * @param {number} page Page selected by the user.
   * @returns {void} Nothing.
   */
  pushRoutePage(page) {
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), `/tasks/${this.tt.taskID}/timelogs`, {
      page: page > 1 ? String(page) : undefined
    }, QUERY_PARAM_UPDATE_OPTIONS)
  }

  /**
   * Replaces invalid route page state with the rendered page.
   * @param {number} page Page that can be rendered.
   * @returns {void} Nothing.
   */
  replaceRoutePage(page) {
    const nextPath = pathForQueryParamUpdates(this.tt.currentQueryParams(), `/tasks/${this.tt.taskID}/timelogs`, {
      page: page > 1 ? String(page) : undefined
    }, QUERY_PARAM_UPDATE_OPTIONS)
    if (nextPath) this.tt.router.replace(nextPath)
  }

  /**
   * Hydrates rows cached before same-route query-param navigation.
   * @param {string} taskID Task identifier for the route.
   * @returns {void} Nothing.
   */
  hydrateCachedRouteState(taskID) {
    const cachedRouteState = routeStateCache.get(taskID)
    if (!cachedRouteState) {
      this.state.timelogs = []
      this.state.timelogsTaskID = ""
      this.state.totalCount = 0
      this.state.subnavCounts = emptyTaskSubnavCounts()
      this.state.task = null
      this.state.loading = true
      this.state.status = null
      return
    }

    this.state.timelogs = cachedRouteState.timelogs
    this.state.timelogsTaskID = taskID
    this.state.totalCount = cachedRouteState.totalCount
    this.state.subnavCounts = cachedRouteState.subnavCounts
    this.state.task = cachedRouteState.task
    this.state.loading = false
  }

  /**
   * Caches loaded rows for same-task route query-param transitions.
   * @returns {void} Nothing.
   */
  cacheLoadedRouteState() {
    routeStateCache.set(this.tt.taskID, {
      subnavCounts: this.s.subnavCounts,
      task: this.s.task,
      timelogs: this.s.timelogs,
      totalCount: this.s.totalCount
    })
  }

  /**
   * Reloads the visible page after a timelog is deleted.
   * @returns {Promise<void>} Resolves after the visible page is refreshed.
   * @rejects {unknown} When reloading fails.
   */
  onTimelogDeleted = async () => {
    const totalCount = Math.max(0, this.s.totalCount - 1)
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
    const nextPage = Math.min(this.s.page, totalPages)

    this.s.totalCount = totalCount
    if (nextPage !== this.s.page) {
      this.tt.pushRoutePage(nextPage)
      this.s.page = nextPage
      return
    }

    await this.tt.loadTimelogs(this.tt.taskID, nextPage, () => false)
  }
}

/** @type {NamedExoticComponent<TasksTimelogsScreenProps>} */
const TasksTimelogsScreenComponent = memo(shapeComponent(TasksTimelogsScreen))

export default TasksTimelogsScreenComponent

/**
 * Parses the task timelog page query param.
 * @param {RouteQueryParams} params Current route query params.
 * @returns {number} Positive page number, defaulting to one.
 */
function pageFromParams(params) {
  const value = optionalRouteParam(params, "page", "Page")
  const parsed = Number.parseInt(value || "", 10)
  if (!Number.isFinite(parsed) || parsed < 1) return 1
  return parsed
}
